import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Icon,
  Divider,
  Avatar,
  Badge,
  Flex,
  useColorModeValue,
  Switch,
} from '@chakra-ui/react';
import {
  User as UserIcon,
  Settings as SettingsIcon,
  Bell as BellIcon,
  HelpCircle as HelpCircleIcon,
  FileText as FileTextIcon,
  LogOut as LogOutIcon,
  Moon as MoonIcon,
  Shield as ShieldIcon,
  MessageCircle as MessageCircleIcon,
  Calendar as CalendarIcon,
  Clock as ClockIcon,
  Award as AwardIcon,
  BookOpen as BookOpenIcon,
  Bookmark as BookmarkIcon,
  ChevronRight as ChevronRightIcon,
  BarChart as BarChartIcon,
} from 'lucide-react';

interface MobileMenuProps {
  onNavigate: (path: string) => void;
  activeDomain: 'AQ' | 'CQ' | 'WQ';
}

const MobileMenu: React.FC<MobileMenuProps> = ({ onNavigate, activeDomain }) => {
  // Colors
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'gray.300');
  const secondaryTextColor = useColorModeValue('gray.500', 'gray.500');
  const methodistBlue = '#0066CC';
  
  // Domain-specific titles
  const domainTitles = {
    AQ: 'SentientIQ Adaptability',
    CQ: 'SentientIQ Culture',
    WQ: 'SentientIQ Workplace'
  };

  // Menu sections
  const profileSection = [
    { icon: UserIcon, label: 'My Profile', path: 'profile' },
    { icon: CalendarIcon, label: 'My Schedule', path: 'schedule' },
    { icon: BookmarkIcon, label: 'Saved Activities', path: 'saved' },
    { icon: AwardIcon, label: 'Achievements', path: 'achievements' },
  ];

  const clinicianSection = [
    { icon: BarChartIcon, label: 'Metrics & Analytics', path: 'metrics' },
    { icon: ClockIcon, label: 'Time Tracking', path: 'timetracking' },
    { icon: BookOpenIcon, label: 'Learning Resources', path: 'resources' },
    { icon: MessageCircleIcon, label: 'Peer Support', path: 'support' },
    { icon: FileTextIcon, label: 'Documentation', path: 'documentation' },
  ];

  const settingsSection = [
    { icon: SettingsIcon, label: 'Settings', path: 'settings' },
    { icon: BellIcon, label: 'Notifications', path: 'notifications' },
    { icon: ShieldIcon, label: 'Privacy', path: 'privacy' },
    { icon: HelpCircleIcon, label: 'Help & Support', path: 'help' },
  ];

  const MenuItem = ({ icon, label, path, hasBadge = false }) => (
    <HStack 
      py={3} 
      px={4} 
      spacing={3} 
      cursor="pointer" 
      onClick={() => onNavigate(path)}
      _hover={{ bg: 'gray.50' }}
      borderRadius="md"
    >
      <Icon as={icon} boxSize={5} color={methodistBlue} />
      <Text flex={1} fontSize="sm" color={textColor}>{label}</Text>
      {hasBadge && (
        <Badge colorScheme="blue" borderRadius="full" fontSize="xs">
          New
        </Badge>
      )}
      <Icon as={ChevronRightIcon} boxSize={4} color="gray.400" />
    </HStack>
  );

  return (
    <Box pt={12} pb={6}>
      {/* Header */}
      <Box px={4} mb={6}>
        <Text fontSize="xl" fontWeight="bold" color={methodistBlue} mb={1}>
          {domainTitles[activeDomain]}
        </Text>
        <Text fontSize="sm" color={secondaryTextColor}>
          Clinician Mobile Experience
        </Text>
      </Box>

      {/* User Profile Summary */}
      <Box px={4} mb={6}>
        <HStack spacing={3} mb={4}>
          <Avatar size="md" name="Dr. Sarah Chen" src="/avatar-placeholder.jpg" />
          <Box>
            <Text fontWeight="medium" fontSize="md">Dr. Sarah Chen</Text>
            <Text fontSize="xs" color={secondaryTextColor}>Attending Physician</Text>
          </Box>
        </HStack>
        <HStack spacing={4}>
          <VStack align="flex-start" spacing={0}>
            <Text fontSize="xs" color={secondaryTextColor}>Domain Score</Text>
            <Text fontWeight="bold" fontSize="lg" color={methodistBlue}>
              {activeDomain === 'AQ' ? '84' : activeDomain === 'CQ' ? '78' : '92'}
            </Text>
          </VStack>
          <VStack align="flex-start" spacing={0}>
            <Text fontSize="xs" color={secondaryTextColor}>Streak</Text>
            <Text fontWeight="bold" fontSize="lg">12 days</Text>
          </VStack>
          <VStack align="flex-start" spacing={0}>
            <Text fontSize="xs" color={secondaryTextColor}>Level</Text>
            <Text fontWeight="bold" fontSize="lg">Advanced</Text>
          </VStack>
        </HStack>
      </Box>

      <Divider mb={4} />

      {/* Profile Section */}
      <Box mb={4}>
        <Text px={4} fontSize="xs" fontWeight="medium" color={secondaryTextColor} mb={2} textTransform="uppercase">
          My Account
        </Text>
        <VStack spacing={0} align="stretch">
          {profileSection.map((item, idx) => (
            <MenuItem 
              key={idx} 
              icon={item.icon} 
              label={item.label} 
              path={item.path} 
              hasBadge={item.label === 'Achievements'}
            />
          ))}
        </VStack>
      </Box>

      {/* Clinician Tools Section */}
      <Box mb={4}>
        <Text px={4} fontSize="xs" fontWeight="medium" color={secondaryTextColor} mb={2} textTransform="uppercase">
          Clinician Tools
        </Text>
        <VStack spacing={0} align="stretch">
          {clinicianSection.map((item, idx) => (
            <MenuItem 
              key={idx} 
              icon={item.icon} 
              label={item.label} 
              path={item.path} 
              hasBadge={item.label === 'Learning Resources'}
            />
          ))}
        </VStack>
      </Box>

      {/* Settings Section */}
      <Box mb={4}>
        <Text px={4} fontSize="xs" fontWeight="medium" color={secondaryTextColor} mb={2} textTransform="uppercase">
          Settings & Support
        </Text>
        <VStack spacing={0} align="stretch">
          {settingsSection.map((item, idx) => (
            <MenuItem 
              key={idx} 
              icon={item.icon} 
              label={item.label} 
              path={item.path}
            />
          ))}
        </VStack>
      </Box>

      {/* Dark Mode Toggle */}
      <Box px={4} py={3}>
        <HStack justify="space-between">
          <HStack spacing={3}>
            <Icon as={MoonIcon} boxSize={5} color={methodistBlue} />
            <Text fontSize="sm" color={textColor}>Dark Mode</Text>
          </HStack>
          <Switch colorScheme="blue" size="md" />
        </HStack>
      </Box>

      <Divider my={4} />

      {/* Logout */}
      <Box px={4}>
        <HStack 
          py={3} 
          spacing={3} 
          cursor="pointer" 
          onClick={() => onNavigate('logout')}
          _hover={{ color: 'red.500' }}
        >
          <Icon as={LogOutIcon} boxSize={5} color="red.500" />
          <Text fontSize="sm" color="red.500">Log Out</Text>
        </HStack>
      </Box>

      {/* App Version */}
      <Box px={4} mt={8} textAlign="center">
        <Text fontSize="xs" color={secondaryTextColor}>
          SentientIQ Mobile v2.5.1
        </Text>
        <Text fontSize="xs" color={secondaryTextColor}>
          2025 Methodist Health System
        </Text>
      </Box>
    </Box>
  );
};

export default MobileMenu;
